import AdminApi from "../api/adminApi";
import AuthApi from "../api/authApi";
import ParishApi from "../api/parishApi";
import ServiceApi from "../api/serviceApi";
import RolesApi from "../api/rolesApi";
import TalksApi from "../api/talksApi";
import ParishCategoryApi from "../api/parishCategoryApi";
import DeaneryApi from "../api/deaneryApi";
import CommitteeApi from "../api/committeeApi";
import RetreatApi from "../api/retreatApi";
import RetreatRosterApi from "../api/retreatRosterApi";
import HelpApi from "../api/helpApi";
import ClosureApi from "../api/closureApi";
import { getSearchAccess } from "./searchAccess";
import { searchPages } from "./searchCatalog";
import {
  asArray,
  coupleTitle,
  formatDateRange,
  joinParts,
  matchesQuery,
  personTitle,
  retreatTitle,
  take,
} from "./searchHelpers";
import type {
  RunGlobalSearchOptions,
  SearchAccess,
  SearchCategory,
  SearchResult,
} from "./searchTypes";
import { SEARCH_LIMIT, ROSTER_RETREAT_CAP } from "./searchTypes";

const adminApi = new AdminApi();
const authApi = new AuthApi();
const parishApi = new ParishApi();
const serviceApi = new ServiceApi();
const rolesApi = new RolesApi();
const talksApi = new TalksApi();
const parishCategoryApi = new ParishCategoryApi();
const deaneryApi = new DeaneryApi();
const committeeApi = new CommitteeApi();
const retreatApi = new RetreatApi();
const rosterApi = new RetreatRosterApi();
const helpApi = new HelpApi();
const closureApi = new ClosureApi();

type SourceFn = () => Promise<SearchResult[]>;

function emit(
  onPartial: RunGlobalSearchOptions["onPartial"],
  results: SearchResult[],
  isCancelled?: () => boolean,
) {
  if (!results.length || isCancelled?.()) return;
  onPartial?.(results);
}

async function safeSource(fn: SourceFn): Promise<SearchResult[]> {
  try {
    return await fn();
  } catch {
    return [];
  }
}

function lifecycleCategory(status?: string): SearchCategory | null {
  const value = String(status || "");
  if (value === "Projected") return "projected";
  if (value === "Pre-Publication Setup" || value === "Ready for Publication") {
    return "inSetup";
  }
  if (value === "Registration Open") return "registrationOpen";
  if (value === "Deleted") return "deleted";
  if (value === "Closing Progress" || value === "Post-Retreat Closing") {
    return "closing";
  }
  return null;
}

function retreatSubtitle(row: Record<string, any>): string {
  return joinParts(
    row.parishName,
    row.language,
    row.location,
    formatDateRange(row.dateFrom, row.dateTo),
    row.id != null ? `ID ${row.id}` : "",
  );
}

function mapRetreatResult(
  row: Record<string, any>,
  category: SearchCategory,
  path: string,
  state?: Record<string, unknown>,
  badge?: string,
): SearchResult {
  return {
    id: `${category}-${row.id}`,
    category,
    title: retreatTitle(row),
    subtitle: retreatSubtitle(row),
    badge: badge || row.lifecycleStatus,
    path,
    state,
  };
}

async function searchCouples(
  query: string,
  access: SearchAccess,
): Promise<SearchResult[]> {
  if (!access.canViewCouples) return [];
  const res: any = await adminApi.getAllCouples({
    userRole: "COUPLE",
    pageNo: 1,
    size: SEARCH_LIMIT,
    searchKeyword: query,
    export: false,
  });
  return take(asArray(res?.data)).map((row) => ({
    id: `couple-${row.id}`,
    category: "couples" as const,
    title: coupleTitle(row),
    subtitle: joinParts(
      row.primaryKey,
      row.parish || row.parishName,
      row.hisEmail || row.herEmail,
      row.accountStatus,
    ),
    badge: row.accountStatus,
    path: "/manageCouples",
    state: { searchKeyword: query, coupleId: row.id },
  }));
}

async function searchMasterData(query: string, access: SearchAccess) {
  if (!access.canViewMasterData) return [] as SearchResult[];
  const key = query.trim();
  const [parishes, services, roles, talks, categories, deaneries] = await Promise.all([
    safeSource(async () => {
      const res: any = await parishApi.searchKey({}, key);
      return take(asArray(res?.data)).map((row) => ({
        id: `parish-${row.id}`,
        category: "masterData" as const,
        title: row.parish || "Parish",
        subtitle: joinParts(row.uniqueId, row.city, row.state, row.zip),
        badge: "Parish",
        path: "/parish",
        state: { searchKeyword: query },
      }));
    }),
    safeSource(async () => {
      const res: any = await serviceApi.searchKey({}, key);
      return take(asArray(res?.data)).map((row) => ({
        id: `service-${row.id}`,
        category: "masterData" as const,
        title: row.service || "Service",
        subtitle: row.spanishService || "",
        badge: "Service",
        path: "/service",
        state: { searchKeyword: query },
      }));
    }),
    safeSource(async () => {
      const res: any = await rolesApi.searchRole({}, key);
      return take(asArray(res?.data)).map((row) => ({
        id: `role-${row.id}`,
        category: "masterData" as const,
        title: row.role || "Role",
        subtitle: row.spanishRole || "",
        badge: "Role",
        path: "/roles",
        state: { searchKeyword: query },
      }));
    }),
    safeSource(async () => {
      const res: any = await talksApi.searchTalk({}, key);
      return take(asArray(res?.data)).map((row) => ({
        id: `talk-${row.id || row.talkId}`,
        category: "masterData" as const,
        title: row.talk || "Talk",
        subtitle: row.spanishTalk || "",
        badge: "Talk",
        path: "/talks",
        state: { searchKeyword: query },
      }));
    }),
    safeSource(async () => {
      const res: any = await parishCategoryApi.searchCategory(key);
      return take(asArray(res?.data)).map((row) => ({
        id: `parish-cat-${row.id}`,
        category: "masterData" as const,
        title: row.name || "Category",
        badge: "Category",
        path: "/parishCategories",
        state: { searchKeyword: query },
      }));
    }),
    safeSource(async () => {
      const res: any = await deaneryApi.searchDeanery(key);
      return take(asArray(res?.data)).map((row) => ({
        id: `deanery-${row.id}`,
        category: "masterData" as const,
        title: row.deaneryName || "Deanery",
        subtitle: joinParts(row.deaneryCode, row.parishCategoryName),
        badge: "Deanery",
        path: "/deaneries",
        state: { searchKeyword: query },
      }));
    }),
  ]);
  return take(
    [...parishes, ...services, ...roles, ...talks, ...categories, ...deaneries],
    8,
  );
}

async function searchAdmins(query: string, access: SearchAccess) {
  if (!access.canViewAdminDashboard) {
    return { admins: [] as SearchResult[], subAdmins: [] as SearchResult[] };
  }
  const res: any = await adminApi.geAdminsAndSubAdmins();
  const mapUser = (
    row: Record<string, any>,
    category: "superAdmins" | "subAdmins",
    tab: string,
  ): SearchResult => ({
    id: `${category}-${row.id}`,
    category,
    title: personTitle(row),
    subtitle: joinParts(row.email, row.userName),
    badge: row.isDisabled ? "Disabled" : undefined,
    path: `/admin-dashboard#${tab}`,
    state: { adminTab: category === "superAdmins" ? "superAdmins" : "subAdmins" },
  });

  const admins = take(
    asArray(res?.admins).filter((row) =>
      matchesQuery(query, row.firstName, row.lastName, row.email, row.userName),
    ),
  ).map((row) => mapUser(row, "superAdmins", "super-admins"));

  const subAdmins = take(
    asArray(res?.subAdmins).filter((row) =>
      matchesQuery(query, row.firstName, row.lastName, row.email, row.userName),
    ),
  ).map((row) => mapUser(row, "subAdmins", "sub-admins"));

  return { admins, subAdmins };
}

async function searchCommittee(query: string, access: SearchAccess) {
  if (!access.canViewAdminDashboard) return [] as SearchResult[];
  const res: any = await committeeApi.getAllCommitteMembers({});
  const users: SearchResult[] = [];
  asArray(res?.data).forEach((parish: any) => {
    asArray(parish?.users).forEach((user: any) => {
      if (
        matchesQuery(
          query,
          user.firstName,
          user.lastName,
          user.email,
          user.eMail,
          parish.parishName,
        )
      ) {
        users.push({
          id: `committee-${user.userId}-${parish.parishId}`,
          category: "committee",
          title: personTitle(user),
          subtitle: joinParts(user.email || user.eMail, parish.parishName),
          path: "/admin-dashboard#steering-committee",
          state: { adminTab: "steeringCommittee" },
        });
      }
    });
  });
  return take(users);
}

async function searchRetreatSubAdmins(query: string, access: SearchAccess) {
  if (!access.canViewAdminDashboard) return [] as SearchResult[];
  const res: any = await retreatApi.allretreatSubAdmins({});
  const hits: SearchResult[] = [];
  asArray(res?.data).forEach((retreat: any) => {
    asArray(retreat?.subAdmins).forEach((admin: any) => {
      if (
        matchesQuery(
          query,
          admin.name,
          admin.email,
          retreat.title,
          retreat.parishName,
          retreat.location,
          String(retreat.id || ""),
        )
      ) {
        hits.push({
          id: `ret-subadmin-${retreat.id}-${admin.id}`,
          category: "retreatSubAdmins",
          title: personTitle(admin),
          subtitle: joinParts(
            retreat.parishName || retreat.title,
            admin.role,
            formatDateRange(retreat.dateFrom, retreat.dateTo),
          ),
          path: "/admin-dashboard#retreat-sub-admins",
          state: { adminTab: "retreatSubAdmins" },
        });
      }
    });
  });
  return take(hits);
}

async function searchRetreatSection(
  query: string,
  access: SearchAccess,
  section: "projected" | "inSetup" | "registrationOpen" | "deleted" | "closing",
): Promise<SearchResult[]> {
  if (!access.canManageRetreats) return [];
  if (section === "projected" && !access.canViewProjected) return [];
  if (section === "closing" && access.canViewClosures) return [];

  const res: any = await retreatApi.listByStatus({
    section,
    searchKeyword: query,
    pageNo: 1,
    size: SEARCH_LIMIT,
    sortColumn: "updatedAt",
    sortDirection: "DESC",
    ...access.actor,
  });

  const category: SearchCategory = section;
  return take(asArray(res?.data)).map((row) => {
    if (section === "projected" || section === "inSetup") {
      return mapRetreatResult(row, category, `/retreat-setup/${row.id}`);
    }
    if (section === "registrationOpen") {
      return mapRetreatResult(
        row,
        category,
        `/retreatRoster?retreatId=${row.id}`,
      );
    }
    if (section === "deleted") {
      return mapRetreatResult(row, category, "/manageRetreat", {
        section: "deleted",
        searchKeyword: query,
      });
    }
    return mapRetreatResult(
      row,
      category,
      access.canViewClosures
        ? "/retreat-closure-list"
        : `/retreatRoster?retreatId=${row.id}`,
      access.canViewClosures ? { searchKeyword: query } : { searchKeyword: query },
    );
  });
}

async function searchAccessedRetreats(
  query: string,
  access: SearchAccess,
  alreadyIds: Set<string>,
): Promise<SearchResult[]> {
  if (!access.canManageRetreats || access.isAdminRole) return [];
  const res: any = await retreatApi.getAllAccessedRetreats({
    userId: access.userId,
  });
  const hits: SearchResult[] = [];
  asArray(res?.data).forEach((row) => {
    const id = String(row.id);
    if (alreadyIds.has(id)) return;
    if (
      !matchesQuery(
        query,
        row.title,
        row.heading,
        row.parishName,
        row.location,
        row.language,
        row.leadCoupleName,
        String(row.id || ""),
      )
    ) {
      return;
    }
    const category = lifecycleCategory(row.lifecycleStatus) || "registrationOpen";
    if (category === "projected" && !access.canViewProjected) return;
    const path =
      category === "projected" || category === "inSetup"
        ? `/retreat-setup/${row.id}`
        : `/retreatRoster?retreatId=${row.id}`;
    hits.push(mapRetreatResult(row, category, path));
  });
  return take(hits);
}

async function searchUpcoming(query: string, access: SearchAccess) {
  if (!access.canViewUpcoming) return [] as SearchResult[];
  const res: any = await authApi.getAllIncommingRetreats();
  const now = new Date();
  const upcoming = asArray(res?.data).filter((row) => {
    const from = row.dateFrom ? new Date(row.dateFrom) : null;
    const isUpcoming = !from || Number.isNaN(from.getTime()) || from >= now;
    return (
      isUpcoming &&
      matchesQuery(
        query,
        row.title,
        row.heading,
        row.parishName,
        row.location,
        row.language,
        String(row.id || ""),
      )
    );
  });
  return take(upcoming).flatMap((row) => [
    {
      id: `upcoming-${row.id}`,
      category: "upcoming" as const,
      title: retreatTitle(row),
      subtitle: retreatSubtitle(row),
      path: `/registration-retreat/${row.id}`,
    },
    {
      id: `registration-${row.id}`,
      category: "registration" as const,
      title: `Registration — ${retreatTitle(row)}`,
      subtitle: retreatSubtitle(row),
      path: `/registration-retreat/${row.id}`,
    },
  ]);
}

async function searchTickets(query: string, access: SearchAccess) {
  if (!access.canViewTickets) return [] as SearchResult[];
  const res: any = await helpApi.getAllTickets({
    pageNo: 1,
    size: SEARCH_LIMIT,
    searchKeyword: query,
    sortBy: {},
    filters: {},
  });
  return take(asArray(res?.data)).map((row) => ({
    id: `ticket-${row.id}`,
    category: "tickets" as const,
    title: row.subject || row.ticketNumber || "Ticket",
    subtitle: joinParts(
      row.ticketNumber,
      [row.firstName, row.lastName].filter(Boolean).join(" "),
      row.status,
      row.priority,
    ),
    badge: row.status,
    path: "/tickets",
    state: { searchKeyword: query },
  }));
}

async function searchClosures(query: string, access: SearchAccess) {
  if (!access.canViewClosures) return [] as SearchResult[];
  const res: any = await closureApi.getAllRetreatClosures({
    pageNo: 1,
    size: SEARCH_LIMIT,
    searchKeyword: query,
    export: false,
  });
  return take(asArray(res?.data)).map((row) => ({
    id: `closing-${row.id}`,
    category: "closing" as const,
    title: retreatTitle(row),
    subtitle: retreatSubtitle(row),
    path: "/retreat-closure-list",
    state: { searchKeyword: query },
  }));
}

async function searchRosterPeople(query: string, access: SearchAccess) {
  if (!access.canViewRoster || access.isFullAdmin) return [] as SearchResult[];
  const res: any = await retreatApi.getAllAccessedRetreats({
    userId: access.userId,
  });
  const retreats = take(asArray(res?.data), ROSTER_RETREAT_CAP);
  const groups = await Promise.all(
    retreats.map((retreat) =>
      safeSource(async () => {
        const rosterRes: any = await rosterApi.getRetreatRoastData({
          retreatId: retreat.id,
          searchKeyword: query,
          pageNo: 1,
          size: 3,
        });
        return asArray(rosterRes?.data).map((row) => ({
          id: `roster-${retreat.id}-${row.coupleId || row.id}`,
          category: "roster" as const,
          title: coupleTitle(row),
          subtitle: joinParts(
            retreatTitle(retreat),
            row.attendeeType,
            row.mainRole,
            row.parish,
          ),
          badge: row.attendeeType,
          path: `/retreatRoster?retreatId=${retreat.id}`,
          state: { searchKeyword: query },
        }));
      }),
    ),
  );
  return take(groups.flat());
}

function rosterShortcuts(retreats: SearchResult[], query: string): SearchResult[] {
  return take(
    retreats
      .filter(
        (item) =>
          item.category === "registrationOpen" ||
          item.category === "inSetup" ||
          item.category === "closing",
      )
      .map((item) => {
        const retreatId =
          String(item.path).match(/retreatId=(\d+)/)?.[1] ||
          String(item.path).match(/retreat-setup\/(\d+)/)?.[1] ||
          String(item.id).split("-").pop();
        return {
          id: `roster-link-${retreatId}`,
          category: "roster" as const,
          title: `Roster — ${item.title}`,
          subtitle: item.subtitle,
          path: `/retreatRoster?retreatId=${retreatId}`,
          state: { searchKeyword: query },
        };
      }),
  );
}

export async function runGlobalSearch(
  query: string,
  user: Record<string, any> | null,
  options: RunGlobalSearchOptions = {},
): Promise<SearchResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const access = getSearchAccess(user);
  if (!access.isAuthenticated) return [];

  const { onPartial, isCancelled } = options;
  const collected: SearchResult[] = [];
  const alreadyIds = new Set<string>();

  const push = (batch: SearchResult[]) => {
    if (isCancelled?.() || !batch.length) return;
    batch.forEach((item) => {
      const retreatId = String(item.id).split("-").pop();
      if (retreatId) alreadyIds.add(retreatId);
    });
    collected.push(...batch);
    emit(onPartial, batch, isCancelled);
  };

  const pageHits = searchPages(q, access);
  push(pageHits);

  const tasks: Array<Promise<void>> = [];

  tasks.push(
    safeSource(() => searchCouples(q, access)).then(push),
  );
  tasks.push(
    safeSource(() => searchMasterData(q, access)).then(push),
  );
  tasks.push(
    (async () => {
      try {
        const res = await searchAdmins(q, access);
        push(res.admins);
        push(res.subAdmins);
      } catch {
        /* ignore source errors */
      }
    })(),
  );
  tasks.push(safeSource(() => searchCommittee(q, access)).then(push));
  tasks.push(
    safeSource(() => searchRetreatSubAdmins(q, access)).then(push),
  );

  const sectionKeys: Array<
    "projected" | "inSetup" | "registrationOpen" | "deleted" | "closing"
  > = ["projected", "inSetup", "registrationOpen", "deleted", "closing"];
  const sectionResults: SearchResult[] = [];
  sectionKeys.forEach((section) => {
    tasks.push(
      safeSource(() => searchRetreatSection(q, access, section)).then((rows) => {
        sectionResults.push(...rows);
        push(rows);
      }),
    );
  });

  tasks.push(safeSource(() => searchUpcoming(q, access)).then(push));
  tasks.push(safeSource(() => searchTickets(q, access)).then(push));
  tasks.push(safeSource(() => searchClosures(q, access)).then(push));
  tasks.push(safeSource(() => searchRosterPeople(q, access)).then(push));

  await Promise.allSettled(tasks);
  if (isCancelled?.()) return collected;

  const extraAccessed = await safeSource(() =>
    searchAccessedRetreats(q, access, alreadyIds),
  );
  push(extraAccessed);

  if (access.isFullAdmin) {
    push(rosterShortcuts([...sectionResults, ...extraAccessed], q));
  }

  return collected;
}
